import { coverageOn, isPartialOn } from './dates.js'

/**
 * When a booking and an absence are a contradiction — the one rule, in one place.
 *
 * It used to be written twice: the month grid ringed a clash in red and the time-off dialog
 * warned about one, each working it out for itself, and the comment in the dialog admitted
 * as much. Two copies of a judgement call is two answers waiting to diverge, and this one is
 * now asked by a third screen — the board's conflict panel — which settles it.
 *
 * The judgement, stated once:
 *
 *   A WHOLE day off with a job on it is a real conflict. Somebody is promised to a job on a
 *   day they are not coming in, and one of those two facts is wrong.
 *
 *   PART of a day off with a job on it is not. Booking around a morning appointment is the
 *   entire point of a window, and the board holds no job times to settle it either way — so
 *   it is offered as "possible", never counted as a fault, and never allowed to put a number
 *   on the board header that a company doing nothing wrong could not clear.
 */

/** What this pair is, on this date: 'conflict', 'possible', or null for no overlap at all. */
export function overlapKind(timeOff, date) {
  const covered = coverageOn(timeOff, date)
  if (!covered) return null
  return isPartialOn(timeOff, date) ? 'possible' : 'conflict'
}

/**
 * Every conflict on a loaded board, newest-dated first-to-last.
 *
 * Read out of the board payload rather than fetched, exactly as the notes drawer is: the
 * board has already been narrowed to the days in view, the mode being shown and the people
 * this actor may see, so a conflict drawn from it cannot be one they were never allowed to
 * know about. Re-querying would reintroduce all three as bugs waiting to happen.
 *
 * @returns [{ key, kind, date, assignment, timeOff, resolved }] — `resolved` is the row from
 *   conflictsResolved when somebody has already said this pair is deliberate, otherwise null.
 */
export function conflictsOn(board) {
  if (!board) return []
  const offByStaff = new Map()
  for (const t of board.timeOff || []) {
    if (!offByStaff.has(t.staffId)) offByStaff.set(t.staffId, [])
    offByStaff.get(t.staffId).push(t)
  }
  const settled = new Map(
    (board.conflictsResolved || []).map((r) => [`${r.assignmentId}:${r.timeOffId}`, r]),
  )

  const out = []
  for (const a of board.assignments || []) {
    for (const t of offByStaff.get(a.staffId) || []) {
      const kind = overlapKind(t, a.date)
      if (!kind) continue
      const key = `${a.id}:${t.id}`
      out.push({ key, kind, date: a.date, assignment: a, timeOff: t, resolved: settled.get(key) || null })
    }
  }
  return out.sort((x, y) => x.date.localeCompare(y.date) || x.assignment.slot - y.assignment.slot)
}

/**
 * What the header badge counts: real conflicts, still open.
 *
 * Not the possibles, and not the settled. A badge that cannot be cleared by doing the right
 * thing is one people learn to ignore, and both exclusions are there to keep it clearable.
 */
export const openConflicts = (list) => list.filter((c) => c.kind === 'conflict' && !c.resolved)
